import React, { useEffect, useRef, useState } from 'react';
import { history, useDispatch, useSelector } from 'umi';
import { Divider, Space, Spin } from 'antd';
import Drawer from '@/components/Drawer';
import * as LocationSelectors from '@/selectors/locationSelectors';
import LeftPanel from './LeftPanel';
import RightPanel from './RightPanel';
import AddServiceOverlay from './AddServiceOverlay';
import NewBranchDrawerSkeleton from './NewBranchDrawerSkeleton';
import styles from './index.less';
import { F, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import isEmpty from 'lodash/isEmpty';

const NewBranchDrawer: React.FC = () => {
  const dispatch = useDispatch();
  const [selectedNavItem, setSelectedNavItem] = useState<string>('details');
  const [isAddServiceOpen, setIsAddServiceOpen] = useState(false);

  const branchDrawer = useSelector(LocationSelectors.selectBranchDrawer);
  const { visible, branch = {}, service: initialServiceId, addService, toggledAt } = branchDrawer || {};

  const isNew = branch?.id === 'NEW';
  const branchData = useSelector((state: DefaultRootState) =>
    LocationSelectors.selectBranchDetail(state, branch?.id),
  );

  const selectedBranch = !isEmpty(branchData)
    ? branchData
    : branch;
  const previousRoute = useRef<string | null>(null);

  const fetchLocationLoading = useSelector(
    (state: DefaultRootState) => state.loading.effects['location/getBranch'],
  );

  const hasBasicData = !!(branch?.name || branch?.address);

  const showSkeleton =
    fetchLocationLoading && !branchData?.id && !hasBasicData && !isNew;

  const serviceTypes = useSelector(LocationSelectors.selectServiceTypeList);

  const fetchServiceTypes = () => {
    dispatch({
      type: 'location/fetchServiceTypes',
    });
  };

  const fetchLocation = (branchId: string) => {
    dispatch({
      type: 'location/getBranch',
      payload: {
        branchId,
      },
    });
  };

  useEffect(() => {
    if (visible && branch?.id && !isNew) {
      fetchLocation(branch.id);
    }
  }, [visible, branch?.id, toggledAt]);

  useEffect(() => {
    if (visible && serviceTypes?.length === 0) {
      fetchServiceTypes();
    }
  }, [visible, serviceTypes?.length]);

  useEffect(() => {
    if (visible && addService) {
      setSelectedNavItem('details');
      setIsAddServiceOpen(true);
    }
  }, [visible, addService, branch?.id]);

  useEffect(() => {
    if (visible) {
      if (initialServiceId) {
        const serviceId = typeof initialServiceId === 'object' ? initialServiceId.id : initialServiceId;
        setSelectedNavItem(serviceId);
      } else {
        setSelectedNavItem('details');
      }
    }
  }, [visible, initialServiceId, toggledAt]);

  useEffect(() => {
    if (visible && branch?.id) {
      if (isNew) return
      previousRoute.current = window.location.pathname;
      window.history.pushState({}, '', `/locations/${branch?.id}`);
    } else if (previousRoute.current) {
      if (previousRoute.current.includes('/locations/')) {
        history.push('/organization/locations');
      } else {
        window.history.pushState({}, '', previousRoute.current);
      }
      previousRoute.current = null;
    }
  }, [visible, branch?.id]);

  const onOpenChange = () => {
    dispatch({
      type: 'location/toggleBranchDrawer',
    });
  };

  const handleAddServiceOverlayClose = (options = {createdService: undefined}) => {
    if (options.createdService){
      setSelectedNavItem(options.createdService?.id)
    }

    setIsAddServiceOpen(false)

  };
  const handleSuccess = (data: any) => {
    if (isNew) {
      dispatch({
        type: 'location/toggleBranchDrawer',
        payload: {
          branch: data?.branch,
        },
      });
    } else if (data?.action === 'deleted') {
      setSelectedNavItem('details');
    }
  };

  return (
    <Drawer
      open={visible}
      onOpenChange={onOpenChange}
      toggledAt={toggledAt}
      title={(() => {
        if (isNew) {
          return <F entity={INTL_ENTITY.BRANCH} action={INTL_ACTION.CREATE} />;
        }

        const services =
          selectedBranch?.services || selectedBranch?.branch_services || [];
        const currentService = services.find((s) => s.id === selectedNavItem);
        if (currentService?.service_type?.name) {
          return (
            <Space size={0}>
              <F entity={INTL_ENTITY.BRANCH} action={INTL_ACTION.DETAILS} />
              <Divider type={"vertical"} size={"small"}/>
              <span>{currentService.service_type.name}</span>
            </Space>
          );
        }

        return <F entity={INTL_ENTITY.BRANCH} action={INTL_ACTION.DETAILS} />;
      })()}
      width={isNew ? 600 : 1000}
      drawerBodyClassName={styles.drawerBody}
    >
      <Spin styles={{
        container: {
          height: '100%',
        },
        root: {
          height: '100%',
        },
      }} spinning={fetchLocationLoading || false}>
        {showSkeleton ? (
          <NewBranchDrawerSkeleton />
        ) : (
          <div className={styles.container} key={toggledAt}>
            <LeftPanel
              branch={selectedBranch}
              selectedNavItem={selectedNavItem}
              onSelectNavItem={setSelectedNavItem}
              onAddService={() => {
                setSelectedNavItem('details');
                setIsAddServiceOpen(true);
              }}
            />
            <div style={{ flex: 1, position: 'relative' }}>
              <RightPanel
                selectedNavItem={selectedNavItem}
                branch={selectedBranch}
                onSuccess={handleSuccess}
              />

              <AddServiceOverlay
                open={isAddServiceOpen}
                branch={selectedBranch}
                onClose={handleAddServiceOverlayClose}
              />
            </div>
          </div>
        )}
      </Spin>
    </Drawer>
  )
    ;
};

export default NewBranchDrawer;
